import React, { useEffect } from "react";
import LmsTable, { TableActionButtonTypes } from "../ui/LmsTable";
import { useParseRouteActions, useFetchData, appendParam, appendParams } from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { FeatureFlags } from "../../constants/LocalServerConstants";

const ADD_EXISTING_DOCUMENT_ROUTE = "add-existing-document";
const UPLOAD_DOCUMENT_ROUTE = "upload";

const setInspectionCompanyFields = (form, isAdd = true, isLoanDisabled = false) => {
  form.addCenteredGroup([form.getDropdownField("Loan", "loans/list-borrower-loans", { disabled: isLoanDisabled || !isAdd })]);

  if (isAdd) {
    form.addCenteredGroup(
      [
        form.getLabelField("Link Option", { width: 2 }),
        form.getGroupRadioField("Link Loan"),
        form.getGroupRadioField("Link Properties"),
        form.getGroupRadioField("Link Draw"),
      ],
      {
        radioGroupName: "linkOption"
      }
    );
  }
  form.addCenteredGroup(
    [
      form.getDropdownField("Loan Property", "loans/loan-properties", {
        dependencyField: "loanId",
        name: "loanPropertyIds",
        dropdownMultiple: true,
        disabled: !isAdd,
      }),
    ],
    {
      isHiddenFunc: ({ inputs }) => inputs.linkOption !== "Link Properties",
    }
  );

  form.addCenteredGroup(
    [
      form.getDropdownField("Loan Draw", "inspections/list-draws-by-loan", {
        dependencyField: "loanId",
        disabled: !isAdd,
      }),
    ],
    {
      isHiddenFunc: ({ inputs }) => inputs.linkOption !== "Link Draw",
    }
  );

  form.addCenteredGroup([
    form.getDropdownField("Work Inspection Company Organization", "inspections/list-inspection-companies", {
      required: true,
    }),
  ]);

  form.addCenteredGroup([
    form.getInputField("Message Subject", { required: true, disabled: !isAdd }),
  ]);

  form.addCenteredGroup([form.getTextareaField("Message Text", { disabled: !isAdd })]);

  return form;
};

const LmsServicingInspectionCompanyMessagesAddForm = ({ respondToId = "", loanId = "", onClosePath = "", loanDrawId = "" }) => {
  const initDataSuffix = loanDrawId
    ? `inspections/company-messages/initial-data?id=${loanDrawId}`
    : respondToId
      ? `inspections/company-messages/${respondToId}`
      : "";

  const [
    {
      data = {
        messageText: "",
        linkOption: loanDrawId ? "Link Draw" : "Link Loan",
        ...(loanId ? { loanId } : {})
      },
      isLoading,
      error,
    },
  ] = useFetchData(initDataSuffix);

  const saveAndContinuePath = appendParam(
    "servicing-inspection-company-messages/upload/{id}",
    "onClosePath",
    onClosePath
  );

  const values = respondToId && data.messageNo ? { replyToMessageNo: data.messageNo } : {};
  const myForm = setInspectionCompanyFields(new BCFormController(data, {
    values,
    submitSuffix: "inspections/company-messages",
    submitMethod: "POST",
    saveAndContinueSuffix: saveAndContinuePath,
    excludeFromSubmit: ["id"]
  }), true, !!loanId);

  const formActions = [
    { action: "save-continue", text: "Upload" },
    { action: "save-close", text: "Send" },
    "close",
  ];

  return <LmsFormGenerator myForm={myForm} actions={formActions} isLoading={isLoading} error={error}/>;
};

const DocumentLogTable = ({ actionButtons, id }: { actionButtons: TableActionButtonTypes[]; id: string }) => (
  <LmsTable
    title="Document Log"
    actionButtons={actionButtons}
    apiSuffix={`loandetails/nwccmessage-document-logs/${id}`}
    deleteSuffix="loandetails/nwccmessage-logs"
    columnLabelsDictionary={{
      nwccfileType: "Inspection Company File Type",
      nwccmessage: "Inspection Company Message",
    }}
    columnClickOptions={{
      documentLog: { downloadPath: "document/download/{id}", idProp: "documentLogId" },
    }}
  />
);

const LmsServicingInspectionCompanyMessagesEditForm = ({ id, navigateToUpload }) => {
  const inspectionEndpoint = `inspections/company-messages/${id}`;
  const [{ data = {}, isLoading, error, fetchData }] = useFetchData(
    `inspections/company-messages/${id}`
  );

  const myForm = setInspectionCompanyFields(
    new BCFormController(data, {
      submitSuffix: "inspections/company-messages",
      values: data,
      initIsDisabled: !data.messageFromNwcc,
      clearSuffixesOnSubmit: [inspectionEndpoint]
    }),
    false
  );

  useEffect(() => {
    if (data.isActive && data?.isRead === false) {
      const markMessageAsRead = async () => {
        await fetchData("inspections/company-messages", {
          fetchMethod: "PUT",
          data: { ...data, isRead: true },
        });
      };

      markMessageAsRead().catch((e) => {
        if (FeatureFlags.IS_DEBUG) {
          console.log(e);
        }
      });
    } else if (data.isActive === false) {
      navigateToUpload();
    }
  }, [data]);

  return (
    <>
      <LmsFormGenerator
        myForm={myForm}
        isLoading={isLoading}
        error={error}
        actions={
          data.messageFromNwcc
            ? [{ action: "save-close", text: "Mark Unread" }, "close"]
            : ["close"]
        }
      />
      <DocumentLogTable id={id} actionButtons={["export"]} />
    </>
  );
};

const LmsServicingInspectionCompanyMessagesUploadForm = ({ id, onClosePath = "" }) => {
  const inspectionEndpoint = `inspections/company-messages/${id}`;
  const [{ data = {}, isLoading, error }] = useFetchData(inspectionEndpoint);
  const myForm = setInspectionCompanyFields(
    new BCFormController(data, {
      submitSuffix: "inspections/company-messages",
      values: data,
      clearSuffixesOnSubmit: [inspectionEndpoint],
    }),
    false
  );

  return (
    <>
      <LmsFormGenerator
        myForm={myForm}
        isLoading={isLoading}
        error={error}
        actions={[{ action: "save-close", text: "Send" }, "close"]}
        onClosePath={onClosePath}
      />
      <DocumentLogTable
        id={id}
        actionButtons={[
          { action: "add", path: `documents/add?loanId=${data?.loanId}&nwccmessageId=${id}` },
          "remove",
          "export",
          {
            action: "add",
            text: "Add Existing Document",
            path: `servicing-inspection-company-messages/${ADD_EXISTING_DOCUMENT_ROUTE}/${id}`,
          },
        ]}
      />
    </>
  );
};

const AddExistingDocumentSubpage = ({ nwccMessageId }) => {
  return <LmsTable
    apiSuffix={`loandetails/document-logs/${nwccMessageId}`}
    actionButtons={["addselected", "close"]}
    addSelectedSuffix={`loandetails/nwccmessage-logs-bulk/${nwccMessageId}`}
    clearSuffixesOnSuccess={[`loandetails/nwccmessage-document-logs/${nwccMessageId}`]}
    columnLabelsDictionary={{
      includeInLrcpolling: "Include In LRC Polling"
    }}
    useApiFiltering
  />;
};

export const LmsServicingInspectionCompanyMessagesMainPage = ({ suffix = "", loanId = "", onClosePath = "" }) => {
  const tableActions = [
    { action: "add", path: appendParams("servicing-inspection-company-messages/add", [{ loanId, onClosePath }]) },
    { action: "edit", path: "servicing-inspection-company-messages" },
    "remove",
    "export",
    {
      action: "edit",
      text: "Respond",
      icon: GlobalConstants.Icons.CARET_UP,
      path: "servicing-inspection-company-messages/add?respondTo={id}"
    }
  ];

  return (
    <LmsTable
      apiSuffix={suffix || "/inspections/company-messages"}
      columnLabelsDictionary={{
        loanNumber: "Loan #",
        messageFromNwcc: "Message From Inspection Company",
        nwccproject: "Inspection Company Project",
      }}
      actionButtons={tableActions}
      sortable={["Message Date"]}
      rowBackgroundColorConditionFunc={(row) => row.isRead === "No"}
      rowBackgroundColor="#ADD8E6"
      onClickPath={"servicing-inspection-company-messages"}
      deleteSuffix="inspections/company-messages"
    />
  );
};

const LmsServicingInspectionCompanyMessages = ({ params = {}, history }) => {
  const {
    isMainPage,
    isAdd,
    isEdit,
    isActionAllowed,
    id,
    isSubpageAllowed,
    actionLower,
    query
  } = useParseRouteActions(
    params,
    ["add", "edit"],
    [UPLOAD_DOCUMENT_ROUTE, ADD_EXISTING_DOCUMENT_ROUTE]
  );

  const loanId = query.get("loanId") || "";
  const loanDrawId = query.get("loanDrawId") || "";
  const respondToId = query.get("respondTo") || "";
  const onClosePath = query.get("onClosePath") || "";

  if (!isActionAllowed) {
    history.goBack();
  }

  const navigateToUpload = () => {
    history.replace(`/servicing-inspection-company-messages/${UPLOAD_DOCUMENT_ROUTE}/${id}`);
  };

  return <>
    {isMainPage && <LmsServicingInspectionCompanyMessagesMainPage/>}
    {isAdd && <LmsServicingInspectionCompanyMessagesAddForm respondToId={respondToId} loanId={loanId} onClosePath={onClosePath} loanDrawId={loanDrawId} />}
    {isEdit && <LmsServicingInspectionCompanyMessagesEditForm id={id} navigateToUpload={navigateToUpload} />}
    {isSubpageAllowed && <>
      {actionLower === UPLOAD_DOCUMENT_ROUTE && <LmsServicingInspectionCompanyMessagesUploadForm id={id} onClosePath={onClosePath}/>}
      {actionLower === ADD_EXISTING_DOCUMENT_ROUTE && <AddExistingDocumentSubpage nwccMessageId={id}/>}
    </>}
  </>;
};

export default LmsServicingInspectionCompanyMessages;
